import {
  FunctionComponent,
  SetStateAction,
  Dispatch,
  useMemo,
  useCallback,
} from 'react'
import {
  MinimalLoader,
  ModalWithHeader,
  ThemedErrorMessage,
  Translation,
  customEvent,
  useWidgetContext,
} from '@dxp-module/common'
import { Login as LoginModule, LoginFormInputOptions } from '@dxp-module/login'
import { labels, errors } from '../../../common/constants/LoginTranslations'
import useCustomerDetails from '../../../common/hooks/queries/useCustomerDetails'
import { MyAccountEvents } from '../../../LoginOrSignup/enum/MyAccountEvents'
import ROUTES from '../../../common/enum/Routes'
import miniWidgetModalStyles from './miniWidgetModal.styles'
import miniWidgetModalControlledInputStyles from './miniWidgetModalControlledInput.styles'

type MiniWidgetModalProps = {
  isOpen: boolean
  toggleModal: Dispatch<SetStateAction<boolean>>
}

const MiniWidgetModal: FunctionComponent<MiniWidgetModalProps> = ({
  isOpen,
  toggleModal,
}) => {
  const styles = miniWidgetModalStyles({})
  const inputStyles = miniWidgetModalControlledInputStyles()

  const {
    configuration: { dealId, myAccountUrl },
  } = useWidgetContext()
  const { customer, error, loading } = useCustomerDetails(dealId)
  const formOptions: LoginFormInputOptions = useMemo(
    () =>
      customer?.email
        ? {
            emailAddress: {
              readonly: true,
              value: customer?.email,
            },
          }
        : {},
    [customer]
  )

  const onLoginSuccess = useCallback(
    (accessToken?: string) => {
      customEvent.emit(
        MyAccountEvents.ON_LOGIN_SUCCESS,
        JSON.stringify({
          dealId: dealId,
          accessToken,
        })
      )

      toggleModal(false)
    },
    [dealId, toggleModal]
  )

  const handleForgotPassword = useCallback(
    () =>
      window.open(`${myAccountUrl || ''}${ROUTES.MYACCOUNT_FORGOT_PASSWORD}`),
    [myAccountUrl]
  )

  if (error) {
    return <ThemedErrorMessage error={error} />
  }

  if (loading) {
    return <MinimalLoader />
  }

  return (
    <ModalWithHeader
      open={isOpen}
      toggleModal={toggleModal}
      title={
        <Translation id="trans__myaccount__login__modal_title_chekout_enquiry_form" />
      }
      content={
        <LoginModule
          labels={{ ...labels, title: '' }}
          errors={errors}
          formInputOptions={formOptions}
          onLoginSuccess={onLoginSuccess}
          onForgotPassword={handleForgotPassword}
          inputClasses={inputStyles}
        />
      }
      classes={{
        closeContainer: styles.closeContainer,
        closeIcon: styles.closeIcon,
        title: styles.title,
        icon: styles.icon,
        content: styles.content,
        modalRoot: styles.modalRoot,
        closeButton: styles.closeButton,
      }}
    />
  )
}

export default MiniWidgetModal
